import { motion } from "framer-motion";

const IMAGES = [
    { src: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80", alt: "Modern luxury home exterior in Udaipur", tag: "Exterior", large: true },
    { src: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=900&q=80", alt: "Premium living room interior design", tag: "Interior" },
    { src: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=900&q=80", alt: "Modular kitchen by Dhanshree Constructions", tag: "Kitchen" },
    { src: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=900&q=80", alt: "Luxury bungalow elevation design", tag: "Elevation" },
    { src: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=900&q=80", alt: "Master bedroom interior", tag: "Bedroom" },
    { src: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=900&q=80", alt: "Premium bathroom design", tag: "Bathroom" },
    { src: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=900&q=80", alt: "Construction site progress", tag: "Build" },
    { src: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=900&q=80", alt: "Modern villa exterior in Rajasthan", tag: "Villa" },
];

export default function Gallery() {
    return (
        <section id="gallery" className="relative bg-brand-bone py-20 md:py-32" data-testid="gallery-section">
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-8 h-px bg-brand-orange" />
                            <span className="text-[10px] uppercase tracking-[0.25em] text-brand-orange font-medium">Gallery</span>
                        </div>
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="font-display font-semibold text-brand-ink text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight"
                            data-testid="gallery-heading"
                        >
                            Homes we've handed over with{" "}
                            <span className="font-serif-italic font-normal brand-gradient-text">love.</span>
                        </motion.h2>
                    </div>
                    <p className="lg:col-span-5 text-brand-muted text-[15px] leading-relaxed">
                        A glimpse into the modern luxury homes, interiors, kitchens and
                        elevations crafted by our team across Udaipur.
                    </p>
                </div>

                {/* Featured large image */}
                <motion.figure
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6 }}
                    className="group relative overflow-hidden rounded-2xl md:rounded-3xl bg-brand-mist aspect-[16/10] md:aspect-[16/8] mb-3 md:mb-4"
                    data-testid="gallery-featured"
                >
                    <img
                        src={IMAGES[0].src}
                        alt={`${IMAGES[0].alt} — Dhanshree Constructions`}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent" />
                    <figcaption className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                        <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-md text-white text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-white/20">
                            {IMAGES[0].tag}
                        </span>
                        <span className="hidden md:block font-display text-sm">Modern Villa · Sukher, Udaipur</span>
                    </figcaption>
                </motion.figure>

                {/* Equal-height grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {IMAGES.slice(1).map((img, i) => (
                        <motion.figure
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                            className="group relative overflow-hidden rounded-2xl bg-brand-mist aspect-[4/5]"
                            data-testid={`gallery-img-${i}`}
                        >
                            <img
                                src={img.src}
                                alt={`${img.alt} — Dhanshree Constructions, Udaipur`}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                            <figcaption className="absolute bottom-3 left-3 right-3 text-white">
                                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-md text-white text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-white/20">
                                    {img.tag}
                                </span>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
}
